import { Dispatch, ReactNode, SetStateAction, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FacetField, FilterOp, FilterState, Item, Member } from '@/features/backlog/types';
import { PRIORITIES, SOURCES, TASK_TYPES, UNASSIGNED, emptyFilters } from '@/features/backlog/constants';
import { useStatuses } from '@/features/backlog/statuses';
import { useEpics } from '@/features/backlog/epics';
import { filtersActive } from '@/features/backlog/filter';
import { Button } from '@/components/ui/Button';
import { FacetSelect, FacetOption, FacetOptionsList } from '@/components/ui/FacetSelect';
import { Popover, menuRow } from '@/components/ui/Popover';
import { SearchField } from '@/components/ui/SearchField';
import { ChevronDown, MoreVertical, PriorityChevron, TaskTypeGlyph } from '@/components/ui/icons';
import { EpicSidebarToggle } from '@/components/epic-sidebar';
import { useItemPanel } from '@/components/item-panel';
import MemberAvatar from '@/components/member-avatar';
import StatusDot from '@/components/status-dot';
import { cn } from '@/components/ui/cn';

type Props = {
  filters: FilterState;
  onChange: Dispatch<SetStateAction<FilterState>>;
  /** Full (unfiltered) item list — used for the status counts and discovered source options. */
  items: Item[];
  members: Member[];
  /** Extra view state (e.g. the Table's sort) the Reset button should also clear. */
  extraActive?: boolean;
  onReset?: () => void;
  /** Inline note shown just to the right of the facets (e.g. the Timeline's record count). */
  meta?: ReactNode;
  /** Page actions (Views, expand/collapse, …) rendered right-aligned on the same toolbar row. */
  trailing?: ReactNode;
};

type FacetDef = {
  key: string;
  label: string;
  options: FacetOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** The is/is-not operator for this facet + its setter (drive the "Is / Is not" toggle). */
  op: FilterOp;
  onOpChange: (op: FilterOp) => void;
};

/**
 * The ⋮ overflow menu: the facets that didn't fit on the row, as an accordion (one expands at a
 * time so a long list like Assignee doesn't blow out the menu). Same option rows as the inline
 * facet popovers. A badge counts how many hidden facets are actively filtering.
 */
function MoreFacets({ facets }: { facets: FacetDef[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const activeCount = facets.reduce((n, f) => n + (f.selected.length > 0 ? 1 : 0), 0);
  return (
    <Popover
      align="right"
      panelClassName="w-[248px] !p-1"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-label="More filters"
          title="More filters"
          className={cn(
            'relative inline-flex min-h-[36px] items-center justify-center rounded-full px-2 transition-colors',
            open || activeCount ? 'bg-accent/15 text-fg' : 'bg-layer text-fg-strong hover:bg-layer-strong',
          )}
        >
          <MoreVertical />
          {activeCount ? (
            <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-accent px-1 text-center text-[10px] font-bold leading-4 text-white">
              {activeCount}
            </span>
          ) : null}
        </button>
      )}
    >
      {() => (
        <div>
          {facets.map((f) => {
            const isOpen = expanded === f.key;
            const n = f.selected.length;
            return (
              <div key={f.key}>
                <button type="button" onClick={() => setExpanded(isOpen ? null : f.key)} className={cn(menuRow, 'justify-between')}>
                  <span className="flex items-center gap-2 text-fg-soft">
                    {f.label}
                    {n ? (
                      <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent/25 px-1 text-[11px] font-semibold text-accent">
                        {n}
                      </span>
                    ) : null}
                  </span>
                  <ChevronDown open={isOpen} className="text-fg-faint" />
                </button>
                {isOpen ? (
                  <div className="mb-1 ml-1 border-l border-line pl-1">
                    <FacetOptionsList options={f.options} selected={f.selected} onChange={f.onChange} op={f.op} onOpChange={f.onOpChange} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Popover>
  );
}

/**
 * The shared search + facet toolbar rendered above the Table, Kanban and Timeline. Each facet
 * uses the same avatar / status-swatch popover the cards use, so filtering feels identical to
 * editing a card. Counts and source/label options derive from the passed (full) item list.
 *
 * The row NEVER wraps to a second line: a hidden measurer sizes every facet, and any that don't
 * fit between the search box and the right-hand actions spill into a ⋮ "more" menu. The search
 * field and the trailing expand/collapse controls stay pinned on the single row at all widths.
 */
export default function FilterBar({ filters, onChange, items, members, extraActive, onReset, meta, trailing }: Props) {
  const { statuses } = useStatuses();
  // The epics toggle lives at the head of this row (before the search icon) and hides while the
  // epic sidebar is actually on screen — the sidebar has its own close button. It can only show on
  // wide layouts (canDock), matching AppLayout's showSidebar gate; on narrow ones the toggle stays.
  const { sidebarOpen } = useEpics();
  const { canDock } = useItemPanel();
  const sidebarVisible = sidebarOpen && canDock;
  const statusCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of items) c[it.status] = (c[it.status] ?? 0) + 1;
    return c;
  }, [items]);

  const statusOptions = useMemo<FacetOption[]>(
    () =>
      statuses.map((s) => ({
        value: s.value,
        label: s.label,
        count: statusCounts[s.value] ?? 0,
        lead: <StatusDot value={s.value} size={16} />,
      })),
    [statuses, statusCounts],
  );

  const priorityCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of items) c[it.priority] = (c[it.priority] ?? 0) + 1;
    return c;
  }, [items]);

  const priorityOptions = useMemo<FacetOption[]>(
    () =>
      PRIORITIES.map((p) => ({
        value: p.value,
        label: p.label,
        count: priorityCounts[p.value] ?? 0,
        lead: (
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            <PriorityChevron level={p.level} color={p.color} />
          </span>
        ),
      })),
    [priorityCounts],
  );

  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of items) c[it.type] = (c[it.type] ?? 0) + 1;
    return c;
  }, [items]);

  const typeOptions = useMemo<FacetOption[]>(
    () =>
      TASK_TYPES.map((t) => ({
        value: t.value,
        label: t.label,
        count: typeCounts[t.value] ?? 0,
        lead: (
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            <TaskTypeGlyph type={t.value} color={t.color} />
          </span>
        ),
      })),
    [typeCounts],
  );

  const assigneeOptions = useMemo<FacetOption[]>(() => {
    const sorted = [...members].sort((a, b) => a.name.localeCompare(b.name));
    return [
      { value: UNASSIGNED, label: 'Unassigned', lead: <MemberAvatar member={undefined} size={20} /> },
      ...sorted.map((m) => ({ value: m.id, label: m.name, lead: <MemberAvatar member={m} size={20} /> })),
    ];
  }, [members]);

  const sourceOptions = useMemo<FacetOption[]>(() => {
    const set = new Set<string>(SOURCES);
    items.forEach((it) => it.source?.forEach((s) => set.add(s)));
    return Array.from(set).map((s) => ({ value: s, label: s }));
  }, [items]);

  const labelOptions = useMemo<FacetOption[]>(() => {
    const set = new Set<string>();
    items.forEach((it) => it.labels?.forEach((l) => set.add(l)));
    return Array.from(set).sort().map((l) => ({ value: l, label: l }));
  }, [items]);

  const facetDefs = useMemo<FacetDef[]>(() => {
    // Each facet maps to a FilterState array field + shares the is/is-not operator wiring (default "is").
    const def = (key: string, label: string, field: FacetField, options: FacetOption[]): FacetDef => ({
      key,
      label,
      options,
      selected: filters[field],
      onChange: (v) => onChange((f) => ({ ...f, [field]: v })),
      op: filters.ops?.[field] ?? 'is',
      onOpChange: (op) => onChange((f) => ({ ...f, ops: { ...f.ops, [field]: op } })),
    });
    return [
      def('status', 'Status', 'statuses', statusOptions),
      def('priority', 'Priority', 'priorities', priorityOptions),
      def('type', 'Type', 'types', typeOptions),
      def('assignee', 'Assignee', 'assignees', assigneeOptions),
      def('source', 'Source', 'sources', sourceOptions),
      def('labels', 'Labels', 'labels', labelOptions),
    ];
  }, [statusOptions, priorityOptions, typeOptions, assigneeOptions, sourceOptions, labelOptions, filters, onChange]);

  const showReset = filtersActive(filters) || !!extraActive;

  // --- Single-row overflow: measure every facet in a hidden row, then show as many as fit -----------
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(facetDefs.length);

  const recompute = useCallback(() => {
    const wrap = wrapRef.current;
    const measure = measureRef.current;
    if (!wrap || !measure) return;
    const GAP = 8; // matches gap-2
    const MORE = 44; // width reserved for the ⋮ button (incl. its gap allowance)
    const SAFETY = 4;
    const containerW = wrap.clientWidth;
    const searchW = searchRef.current?.offsetWidth ?? 0;
    const rightW = rightRef.current?.offsetWidth ?? 0;
    const facetW = Array.from(measure.querySelectorAll<HTMLElement>('[data-mf]')).map((e) => e.offsetWidth);
    // Space left for the facet zone once the search box, the right-hand actions and the two gaps
    // that flank the facets (search|facets and facets|right) are accounted for.
    const base = containerW - searchW - rightW - GAP * 2 - SAFETY;
    const fit = (budget: number) => {
      let used = 0;
      let n = 0;
      for (let i = 0; i < facetW.length; i++) {
        const add = (n === 0 ? 0 : GAP) + facetW[i];
        if (used + add <= budget) {
          used += add;
          n += 1;
        } else break;
      }
      return n;
    };
    let n = fit(base);
    if (n < facetW.length) n = fit(base - GAP - MORE); // make room for the ⋮ button, then refit
    setVisible(Math.max(0, Math.min(n, facetW.length)));
  }, []);

  useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(() => recompute());
    [wrapRef, searchRef, rightRef, measureRef].forEach((r) => {
      if (r.current) ro.observe(r.current);
    });
    return () => ro.disconnect();
  }, [recompute]);

  const shown = facetDefs.slice(0, visible);
  const hidden = facetDefs.slice(visible);

  return (
    <div ref={wrapRef} className="relative mb-3 flex items-center gap-2 overflow-x-clip">
      <div ref={searchRef} className="flex shrink-0 items-center gap-2">
        {!sidebarVisible ? <EpicSidebarToggle /> : null}
        <SearchField value={filters.search} onChange={(v) => onChange((f) => ({ ...f, search: v }))} />
      </div>
      {shown.map((f) => (
        <div key={f.key} className="shrink-0">
          <FacetSelect label={f.label} options={f.options} selected={f.selected} onChange={f.onChange} op={f.op} onOpChange={f.onOpChange} />
        </div>
      ))}
      {hidden.length ? (
        <div className="shrink-0">
          <MoreFacets facets={hidden} />
        </div>
      ) : null}
      <div ref={rightRef} className="ml-auto flex shrink-0 items-center gap-2">
        {showReset ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(emptyFilters());
              onReset?.();
            }}
          >
            Reset
          </Button>
        ) : null}
        {meta ? <span className="whitespace-nowrap text-[13px] font-medium text-fg-faint">{meta}</span> : null}
        {trailing}
      </div>

      {/* Hidden measurer — every facet at natural width, so recompute knows what fits. */}
      <div ref={measureRef} aria-hidden className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2">
        {facetDefs.map((f) => (
          <span key={f.key} data-mf className="shrink-0">
            <FacetSelect label={f.label} options={f.options} selected={f.selected} onChange={f.onChange} op={f.op} onOpChange={f.onOpChange} />
          </span>
        ))}
      </div>
    </div>
  );
}
